import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, MoreVertical, Loader2, Sparkles, Plus, Edit, Trash2, Copy, Archive, Play, BookOpen, BarChart2, BarChart3 } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import AISurveyGenerator from '../components/Surveys/AISurveyGenerator';
import SurveyDetailModal from '../components/Surveys/SurveyDetailModal';
import SurveysAnalyticsDrawer from '../components/Analytics/SurveysAnalyticsDrawer';
import AnalyticsDrawer from '../components/AnalyticsDrawer';

const AdminSurveysPage = () => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');

    // Modal state
    const [showAISurveyGenerator, setShowAISurveyGenerator] = useState(false);
    const [editingSurvey, setEditingSurvey] = useState(null);

    // Detail modal state
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedSurveyId, setSelectedSurveyId] = useState(null);

    // Page Level Analytics State
    const [isPageAnalyticsOpen, setIsPageAnalyticsOpen] = useState(false);

    // Actions dropdown state
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const dropdownRef = useRef(null);

    // Per-survey analytics drawer
    const [drawerContext, setDrawerContext] = useState(null);

    const tabs = ['All', 'Active', 'Scheduled', 'Draft', 'Completed', 'Archived'];

    useEffect(() => {
        fetchSurveys();
    }, []);

    const fetchSurveys = async () => {
        try {
            setLoading(true);
            const res = await api.get('/surveys');
            // Handle both { surveys: [...] } and [...] formats, default to []
            const data = res.data.surveys || res.data;
            setSurveys(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to fetch surveys", error);
            toast.error("Failed to load surveys");
            setSurveys([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredSurveys = surveys.filter(survey => {
        const matchesTab = activeTab === 'All' || survey.status?.toLowerCase() === activeTab.toLowerCase();
        const matchesSearch = survey.title.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesTab && matchesSearch;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-700';
            case 'draft': return 'bg-gray-100 text-gray-700';
            case 'scheduled': return 'bg-blue-100 text-blue-700';
            case 'completed': return 'bg-purple-100 text-purple-700';
            case 'archived': return 'bg-yellow-100 text-yellow-700';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const handleOpenAIGenerator = (survey = null) => {
        setEditingSurvey(survey);
        setShowAISurveyGenerator(true);
    };

    const handleCloseAIGenerator = () => {
        setShowAISurveyGenerator(false);
        setEditingSurvey(null);
    };

    const handleSurveySuccess = () => {
        fetchSurveys();
        handleCloseAIGenerator();
    };

    const handleOpenDetail = (surveyId) => {
        setSelectedSurveyId(surveyId);
        setShowDetailModal(true);
    };

    const handleCloseDetail = () => {
        setShowDetailModal(false);
        setSelectedSurveyId(null);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdownId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Survey actions
    const handleDeleteSurvey = async (e, surveyId) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to archive this survey? It will be moved to the Archived tab.')) {
            try {
                await api.delete(`/surveys/${surveyId}`);
                toast.success('Survey archived');
                fetchSurveys();
            } catch (error) {
                toast.error('Failed to archive survey');
            }
        }
        setOpenDropdownId(null);
    };

    const handlePermanentDelete = async (e, surveyId) => {
        e.stopPropagation();
        if (window.confirm('⚠️ PERMANENT DELETE\n\nThis will permanently delete the survey and ALL responses. This action cannot be undone.\n\nAre you sure you want to continue?')) {
            try {
                await api.delete(`/surveys/${surveyId}/permanent`);
                toast.success('Survey permanently deleted');
                fetchSurveys();
            } catch (error) {
                toast.error(error.response?.data?.error || 'Failed to delete survey');
            }
        }
        setOpenDropdownId(null);
    };

    const handleDuplicateSurvey = async (e, survey) => {
        e.stopPropagation();
        try {
            await api.post('/surveys', {
                title: `${survey.title} (Copy)`,
                description: survey.description,
                trigger_type: survey.trigger_type,
                trigger_config: survey.trigger_config,
                target_audience_type: survey.target_audience_type,
                target_audience_config: survey.target_audience_config,
                is_compliance_required: survey.is_compliance_required,
                is_anonymous: survey.is_anonymous,
                questions: survey.questions
            });
            toast.success('Survey duplicated');
            fetchSurveys();
        } catch (error) {
            toast.error('Failed to duplicate survey');
        }
        setOpenDropdownId(null);
    };

    const handleArchiveSurvey = async (e, surveyId) => {
        e.stopPropagation();
        try {
            await api.put(`/surveys/${surveyId}`, { status: 'archived' });
            toast.success('Survey archived');
            fetchSurveys();
        } catch (error) {
            toast.error('Failed to archive survey');
        }
        setOpenDropdownId(null);
    };

    const handleActivateSurvey = async (e, surveyId) => {
        e.stopPropagation();
        try {
            // Activate the survey
            await api.put(`/surveys/${surveyId}`, { status: 'active' });
            // Deploy to target audience
            const deployRes = await api.post(`/surveys/${surveyId}/deploy`);
            const deployedCount = deployRes.data.deployedCount || deployRes.data.message || 'employees';
            toast.success(`Survey activated and deployed to ${deployedCount} ${typeof deployedCount === 'number' ? 'employees' : ''}!`);
            fetchSurveys();
        } catch (error) {
            console.error('Activation error:', error);
            toast.error(error.response?.data?.error || 'Failed to activate survey');
        }
        setOpenDropdownId(null);
    };

    return (
        <div className="p-6 max-w-[1280px] mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Activity Centre — Surveys</h1>
                        <p className="text-sm text-gray-500">Manage employee surveys, check-ins, and feedback loops.</p>
                    </div>
                    <div className="flex gap-2.5 flex-wrap">
                        <button
                            onClick={() => handleOpenAIGenerator()}
                            className="h-9 bg-accent text-white border-0 rounded-md px-3.5 text-sm font-semibold cursor-pointer flex items-center gap-1.5 hover:bg-accent/90 transition-colors"
                        >
                            <Plus size={15} />
                            Create Survey
                        </button>
                        <button
                            onClick={() => setIsPageAnalyticsOpen(true)}
                            className="h-9 bg-white text-indigo-600 border border-indigo-200 rounded-md px-3.5 text-sm font-semibold cursor-pointer flex items-center gap-1.5 hover:bg-indigo-50 shadow-sm transition-colors"
                        >
                            <BarChart2 size={15} />
                            View Insights
                        </button>

                    </div>
                </div>
            </div>


            {/* AI Survey Generator Modal */}
            <AISurveyGenerator
                isOpen={showAISurveyGenerator}
                onClose={handleCloseAIGenerator}
                onSuccess={handleSurveySuccess}
                editSurvey={editingSurvey}
            />

            {/* Tabs */}
            <div className="mb-6 border-b border-gray-200">
                <nav className="flex gap-6 overflow-x-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`py-3 text-sm font-medium bg-transparent border-0 border-b-2 -mb-px transition-colors whitespace-nowrap cursor-pointer ${activeTab === tab
                                ? 'border-accent text-accent'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Filter Bar */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 mb-5 flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[250px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search surveys..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 h-9 border border-gray-200 rounded-lg text-sm outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all"
                    />
                </div>
            </div>

            {/* Survey Cards */}
            <div className="mb-6">
                {loading ? (
                    <div className="p-12 text-center bg-white border border-gray-200 rounded-lg">
                        <Loader2 size={32} className="animate-spin text-accent mx-auto" />
                    </div>
                ) : filteredSurveys.length === 0 ? (
                    <div className="p-12 text-center bg-white border border-gray-200 rounded-lg">
                        <Search size={32} className="text-gray-400 mx-auto mb-3" />
                        <h3 className="text-base font-semibold text-gray-900 mb-2">No surveys found</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            {searchQuery ? "Try adjusting your search filters." : "Get started by creating your first survey."}
                        </p>
                        {!searchQuery && (
                            <div className="flex gap-2.5 justify-center">
                                <button
                                    onClick={() => handleOpenAIGenerator()}
                                    className="h-9 bg-accent text-white border-0 rounded-md px-4 text-sm font-semibold cursor-pointer flex items-center gap-1.5 hover:bg-accent/90 transition-colors"
                                >
                                    <Plus size={15} /> Create Survey
                                </button>

                            </div>
                        )}
                    </div>
                ) : (
                    filteredSurveys.map((survey) => (
                        <div
                            key={survey.id}
                            onClick={() => handleOpenDetail(survey.id)}
                            className="bg-white border border-gray-200 rounded-lg p-4 mb-3 cursor-pointer transition-all hover:border-accent hover:shadow-sm"
                        >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                {/* Left: Title & metadata */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
                                        <span className="font-semibold text-[15px] text-gray-900">{survey.title}</span>
                                        <span className={`${getStatusColor(survey.status)} px-2.5 py-0.5 rounded-full text-[11px] font-semibold capitalize`}>
                                            {survey.status}
                                        </span>
                                    </div>
                                    {survey.created_via_course_id && (
                                        <div className="inline-flex items-center gap-1 text-[11px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded mb-1.5">
                                            <BookOpen className="w-3 h-3" />
                                            Course: {survey.course_name}
                                        </div>
                                    )}
                                    {survey.description && (
                                        <p className="text-gray-500 text-[13px] m-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-[500px]">{survey.description}</p>
                                    )}
                                    <div className="flex gap-4 mt-2 text-xs text-gray-500">
                                        <span>Trigger: <strong className="text-gray-900 capitalize">{survey.trigger_type?.replace('_', ' ')}</strong></span>
                                        <span>Audience: <strong className="text-gray-900 capitalize">{survey.target_audience_type}</strong></span>
                                        <span>Created: <strong className="text-gray-900">{new Date(survey.created_at).toLocaleDateString()}</strong></span>
                                    </div>
                                </div>

                                {/* Right: Stats button + Actions dropdown */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {survey.status === 'active' && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDrawerContext({ type: 'survey', id: survey.id, title: survey.title });
                                            }}
                                            className="h-8 px-3 text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-md flex items-center gap-1.5 transition-colors"
                                        >
                                            <BarChart3 size={14} /> Stats
                                        </button>
                                    )}
                                    <div className="relative" ref={openDropdownId === survey.id ? dropdownRef : null}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenDropdownId(openDropdownId === survey.id ? null : survey.id);
                                            }}
                                            className="w-8 h-8 border border-gray-200 rounded-md bg-white flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            <MoreVertical size={16} />
                                        </button>

                                        {openDropdownId === survey.id && (
                                            <div className="absolute right-0 top-[38px] z-20 w-[180px] bg-white rounded-md shadow-lg border border-gray-200 py-1">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setOpenDropdownId(null); setEditingSurvey(survey); setShowAISurveyGenerator(true); }}
                                                    className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-gray-900 hover:bg-gray-50 transition-colors text-left"
                                                >
                                                    <Edit size={14} /> Edit Survey
                                                </button>
                                                <button
                                                    onClick={(e) => handleDuplicateSurvey(e, survey)}
                                                    className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-gray-900 hover:bg-gray-50 transition-colors text-left"
                                                >
                                                    <Copy size={14} /> Duplicate
                                                </button>
                                                {survey.status !== 'active' && (
                                                    <button
                                                        onClick={(e) => handleActivateSurvey(e, survey.id)}
                                                        className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-green-600 hover:bg-green-50 transition-colors text-left"
                                                    >
                                                        <Play size={14} /> Activate
                                                    </button>
                                                )}
                                                {survey.status !== 'archived' && (
                                                    <button
                                                        onClick={(e) => handleArchiveSurvey(e, survey.id)}
                                                        className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-gray-900 hover:bg-gray-50 transition-colors text-left"
                                                    >
                                                        <Archive size={14} /> Archive
                                                    </button>
                                                )}
                                                <hr className="my-1 border-gray-100" />
                                                {survey.status === 'archived' ? (
                                                    <button
                                                        onClick={(e) => handlePermanentDelete(e, survey.id)}
                                                        className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors text-left"
                                                    >
                                                        <Trash2 size={14} /> Delete Permanently
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={(e) => handleDeleteSurvey(e, survey.id)}
                                                        className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors text-left"
                                                    >
                                                        <Trash2 size={14} /> Delete
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Survey Detail Modal */}
            <SurveyDetailModal
                isOpen={showDetailModal}
                onClose={handleCloseDetail}
                surveyId={selectedSurveyId}
                onUpdate={fetchSurveys}
            />
            {/* Page Level Analytics Drawer */}
            <SurveysAnalyticsDrawer
                isOpen={isPageAnalyticsOpen}
                onClose={() => setIsPageAnalyticsOpen(false)}
            />
            {/* Per-survey Analytics Drawer */}
            <AnalyticsDrawer
                isOpen={!!drawerContext}
                onClose={() => setDrawerContext(null)}
                context={drawerContext}
            />
        </div>
    );
};

export default AdminSurveysPage;
